import { useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { CommentBody, MentionTextarea, useMentions } from '@/components/ui/mentions';
import remarkGfm from 'remark-gfm';
import {
  AlignLeft,
  ArrowLeft,
  Check,
  ClipboardCheck,
  CornerUpLeft,
  FileText,
  History,
  Info,
  ListChecks,
  MessageSquare,
  Send,
} from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import { deliverableLabel, deliverablesKey, useDeliverable, type DeliverableStatus } from '@/lib/deliverables';
import { useDocuments } from '@/lib/documents';
import { trackerKey, useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { cn, fmtDate, fmtDateTime, fmtRelative } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input, Select, Textarea, Field } from '@/components/ui/field';
import { Alert, Badge, Card, CardHeader, Spinner } from '@/components/ui/misc';
import { Dialog } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toast';
import { Avatar, StatusPill } from '@/components/tracker/bits';
import { DocumentList } from '@/components/documents/DocumentList';
import { useOpenTask } from '@/components/tracker/TaskPanel';
import type { TaskStatus } from '@/lib/tracker';
import { AwaitingDays, DeliverableStatusBadge } from './DeliverablesPage';
import { AiBadge } from '@/components/ai/Assistant';
import { ChecklistEditor, DeliverableAi } from '@/components/ai/DeliverableReview';

const STEPS: DeliverableStatus[] = ['NOT_STARTED', 'DRAFTING', 'INTERNAL_REVIEW', 'SUBMITTED', 'ACCEPTED'];

/** Wording for each move button, from the mover's point of view. */
const MOVE_LABEL: Record<DeliverableStatus, string> = {
  NOT_STARTED: 'Back to not started',
  DRAFTING: 'Start drafting',
  INTERNAL_REVIEW: 'Send to internal review',
  SUBMITTED: 'Submit for review',
  REVISIONS_REQUESTED: 'Ask for revisions',
  ACCEPTED: 'Accept',
};
const NEEDS_NOTE = (from: DeliverableStatus, to: DeliverableStatus) =>
  to === 'REVISIONS_REQUESTED' || (from === 'SUBMITTED' && to === 'INTERNAL_REVIEW');

export function DeliverableDetail() {
  const program = useOutletContext<Program>();
  const { deliverableId = '' } = useParams();
  const q = useDeliverable(program.id, deliverableId);
  const docs = useDocuments(program.id, { deliverableId });
  const tracker = useTracker(program.id);
  const qc = useQueryClient();
  const toast = useToast();
  const openTask = useOpenTask();
  const [pending, setPending] = useState<DeliverableStatus | null>(null);
  const [note, setNote] = useState('');
  const mentions = useMentions();
  const [editingChecklist, setEditingChecklist] = useState(false);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: deliverablesKey(program.id) });
    void qc.invalidateQueries({ queryKey: trackerKey(program.id) });
    void qc.invalidateQueries({ queryKey: ['documents', program.id] });
  };
  const move = useMutation({
    mutationFn: (v: { to: DeliverableStatus; note?: string }) =>
      api.post(`/api/programs/${program.id}/deliverables/${deliverableId}/status`, v),
    onSuccess: (_r, v) => {
      toast.ok(`${MOVE_LABEL[v.to].replace(/^Back to /, 'Moved to ')}: done.`);
      setPending(null);
      setNote('');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/programs/${program.id}/deliverables/${deliverableId}`, body),
    onSuccess: refresh,
    onError: (e) => toast.error(errorMessage(e)),
  });
  const addComment = useMutation({
    mutationFn: (body: string) =>
      api.post(`/api/programs/${program.id}/deliverables/${deliverableId}/comments`, { body }),
    onSuccess: () => {
      mentions.reset();
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (q.isLoading) return <Spinner className="h-full" />;
  if (!q.data) return <p className="p-8 text-sm text-muted">{q.error ? errorMessage(q.error) : 'Not found.'}</p>;
  const d = q.data;
  const client = program.clientShortName;
  const phase = tracker.data?.phases.find((p) => p.id === d.phaseId);
  const executives = tracker.data?.members.filter((m) => m.role === 'EXECUTIVE') ?? [];
  const locked = d.status === 'SUBMITTED' || d.status === 'ACCEPTED';
  const stepIndex = STEPS.indexOf(d.status === 'REVISIONS_REQUESTED' ? 'DRAFTING' : d.status);

  const doMove = (to: DeliverableStatus) => (NEEDS_NOTE(d.status, to) ? setPending(to) : move.mutate({ to }));

  return (
    <div className="mx-auto max-w-[1200px] px-6 py-5">
      <Link
        to={`/p/${program.id}/deliverables`}
        className="mb-3 inline-flex items-center gap-1 text-xs text-muted hover:text-text"
      >
        <ArrowLeft className="size-3.5" /> Deliverables
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-subtle">
            {phase ? `Phase ${phase.number} · ${phase.name}` : 'Deliverable'}
          </p>
          <h1 className="mt-0.5 flex items-center gap-2 text-xl font-semibold tracking-tight">
            {d.name}
            {d.isProgramLevel && <Badge tone="brand">PA §4</Badge>}
          </h1>
          {d.aliases.length > 0 && (
            <p className="mt-0.5 text-xs text-muted">The Project Assignment also calls it: {d.aliases.join(', ')}</p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {d.moves.map((to) => (
            <Button
              key={to}
              variant={
                to === 'ACCEPTED' || to === 'SUBMITTED'
                  ? 'primary'
                  : to === 'REVISIONS_REQUESTED'
                    ? 'danger'
                    : 'secondary'
              }
              loading={move.isPending && move.variables?.to === to}
              onClick={() => doMove(to)}
            >
              {to === 'ACCEPTED' ? (
                <Check />
              ) : to === 'SUBMITTED' ? (
                <Send />
              ) : to === 'REVISIONS_REQUESTED' ? (
                <CornerUpLeft />
              ) : null}
              {d.status === 'SUBMITTED' && to === 'INTERNAL_REVIEW' ? 'Withdraw submission' : MOVE_LABEL[to]}
            </Button>
          ))}
        </div>
      </div>

      {/* Lifecycle */}
      <Card className="mt-4 px-5 py-4">
        <ol className="flex flex-wrap items-center gap-2 text-xs">
          {STEPS.map((s, i) => (
            <li key={s} className="flex items-center gap-2">
              <span
                className={cn(
                  'flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium',
                  i < stepIndex
                    ? 'bg-ok-soft text-ok'
                    : i === stepIndex
                      ? 'bg-brand text-white'
                      : 'bg-surface-2 text-subtle',
                )}
              >
                {i < stepIndex && <Check className="size-3" />}
                {deliverableLabel(s, client)}
              </span>
              {i < STEPS.length - 1 && <span className="h-px w-5 bg-border-strong" />}
            </li>
          ))}
        </ol>
        {d.status === 'REVISIONS_REQUESTED' && (
          <Alert tone="bad" className="mt-3" title={`${client} asked for revisions`}>
            {d.events.filter((e) => e.to === 'REVISIONS_REQUESTED').at(-1)?.note}
          </Alert>
        )}
        <div className="mt-3 flex flex-wrap gap-x-8 gap-y-1 text-[13px] text-muted">
          <span>
            Status: <DeliverableStatusBadge status={d.status} client={client} />
          </span>
          {d.status === 'SUBMITTED' && (
            <span>
              Awaiting review: <AwaitingDays days={d.daysAwaiting} />
            </span>
          )}
          {d.acceptedAt && <span>Accepted {fmtDate(d.acceptedAt.slice(0, 10))}</span>}
          <span>Current version: {d.currentVersion ? `v${d.currentVersion}` : 'none filed'}</span>
        </div>
      </Card>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader
              title="Document"
              icon={<FileText />}
              description={
                locked
                  ? `Locked while ${d.status === 'ACCEPTED' ? 'accepted' : `with ${client} for review`}; versions match what was submitted.`
                  : 'Each upload becomes the next version; earlier versions are kept.'
              }
            />
            <div className="px-5 py-2">
              {d.isAdmin && (
                <div className="pb-2 pt-1">
                  <DeliverableAi
                    program={program}
                    deliverableId={d.id}
                    versions={docs.data?.find((x) => x.type === 'DELIVERABLE')?.versions.map((v) => v.version) ?? []}
                    hasChecklist={d.reviewChecklist.length > 0}
                    onPosted={refresh}
                  />
                </div>
              )}
              <DocumentList
                program={program}
                docs={docs.data?.filter((x) => x.type === 'DELIVERABLE')}
                loading={docs.isLoading}
                uploadTarget={{ deliverableId: d.id, label: d.name }}
                canUpload={d.isAdmin && !locked}
                emptyText="No version filed yet. File the document before submitting."
              />
              {(docs.data?.filter((x) => x.type !== 'DELIVERABLE').length ?? 0) > 0 && (
                <>
                  <h4 className="mt-3 text-[11px] font-semibold uppercase tracking-wide text-subtle">
                    Supporting files
                  </h4>
                  <DocumentList program={program} docs={docs.data?.filter((x) => x.type !== 'DELIVERABLE')} />
                </>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Description" icon={<AlignLeft />} tone="neutral" />
            <div className="prose-helm px-5 py-4 text-[13.5px]">
              {d.description ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{d.description}</ReactMarkdown>
              ) : (
                <p className="text-muted">No description yet.</p>
              )}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Submission log"
              icon={<History />}
              tone="info"
              description="The written record of delivery and acceptance (Agreement §3)."
            />
            <ol className="px-5 py-3 text-[13px]">
              {d.events.map((e) => (
                <li key={e.id} className="flex gap-3 border-b border-border py-2 last:border-0">
                  <span className="w-36 shrink-0 text-xs tabular text-muted" title={fmtDateTime(e.at)}>
                    {fmtDateTime(e.at)}
                  </span>
                  <span className="flex-1">
                    <span className="font-medium">{e.actor}</span> moved it to{' '}
                    {deliverableLabel(e.to, client).toLowerCase()}
                    {e.version ? ` (v${e.version})` : ''}
                    {e.note && <span className="block text-xs text-muted">“{e.note}”</span>}
                  </span>
                </li>
              ))}
              {!d.events.length && <li className="py-2 text-muted">No moves yet.</li>}
            </ol>
          </Card>

          <Card>
            <CardHeader title="Comments" icon={<MessageSquare />} />
            <div className="flex flex-col gap-3 px-5 py-4">
              {d.comments.map((c) => (
                <div key={c.id} className="flex gap-2.5">
                  <Avatar name={c.author.name} size={24} />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs">
                      <span className="font-medium">{c.author.name}</span>{' '}
                      <span className="text-subtle">{fmtRelative(c.createdAt)}</span> <AiBadge runId={c.aiRunId} />
                    </p>
                    <div className="prose-helm text-[13px]">
                      <CommentBody body={c.body} />
                    </div>
                  </div>
                </div>
              ))}
              <form
                className="flex flex-col gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (mentions.text.trim()) addComment.mutate(mentions.body());
                }}
              >
                <MentionTextarea
                  program={program}
                  m={mentions}
                  rows={2}
                  placeholder="Write a comment. Markdown supported."
                />
                <div>
                  <Button type="submit" size="sm" loading={addComment.isPending} disabled={!mentions.text.trim()}>
                    Comment
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>

        <div className="flex flex-col gap-4">
          <Card>
            <CardHeader title="Details" icon={<Info />} tone="neutral" />
            <dl className="grid grid-cols-[100px_1fr] items-center gap-x-3 gap-y-2.5 px-5 py-4 text-[13px]">
              <dt className="text-xs text-muted">Target</dt>
              <dd>
                {d.isAdmin ? (
                  <Input
                    type="date"
                    aria-label="Target date"
                    className="h-8 text-[13px]"
                    defaultValue={d.targetDate ?? ''}
                    key={d.targetDate ?? 'none'}
                    onBlur={(e) =>
                      (e.target.value || null) !== d.targetDate && patch.mutate({ targetDate: e.target.value || null })
                    }
                  />
                ) : d.targetDate ? (
                  fmtDate(d.targetDate)
                ) : (
                  'Not set'
                )}
              </dd>
              <dt className="text-xs text-muted">Approver</dt>
              <dd>
                {d.isAdmin ? (
                  <Select
                    aria-label="Approver"
                    className="h-8 text-[13px]"
                    value={d.approverDelegated ? (d.approver?.id ?? '') : ''}
                    onChange={(e) => patch.mutate({ approverUserId: e.target.value || null })}
                  >
                    <option value="">
                      Default approver{!d.approverDelegated && d.approver ? ` (${d.approver.name})` : ''}
                    </option>
                    {executives.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  (d.approver?.name ?? 'Not designated yet')
                )}
              </dd>
            </dl>
            {!d.approver && (
              <p className="px-5 pb-4 text-xs text-warn">
                No one can accept yet: link the default approver seat to a login in Admin, or delegate to an executive.
              </p>
            )}
          </Card>

          <Card>
            <CardHeader title="Tasks producing it" icon={<ListChecks />} tone="ok" />
            <ul className="px-2 py-2">
              {d.tasks.map((t) => (
                <li key={t.id}>
                  <button
                    className="flex w-full items-center gap-2 rounded px-3 py-1.5 text-left text-[13px] hover:bg-surface-2"
                    onClick={() => openTask(t.id)}
                  >
                    <StatusPill status={t.status as TaskStatus} />
                    <span className="truncate">{t.title}</span>
                  </button>
                </li>
              ))}
              {!d.tasks.length && (
                <li className="px-3 py-1.5 text-[13px] text-muted">No tasks linked. Link one from a task's panel.</li>
              )}
            </ul>
          </Card>

          <Card>
            <CardHeader
              title="Review checklist"
              icon={<ClipboardCheck />}
              tone="warn"
              description="BlackWolf's standard for this document type."
              actions={
                d.isAdmin && !editingChecklist ? (
                  <Button size="sm" variant="ghost" onClick={() => setEditingChecklist(true)}>
                    Edit
                  </Button>
                ) : undefined
              }
            />
            {editingChecklist ? (
              <ChecklistEditor
                value={d.reviewChecklist}
                saving={patch.isPending}
                onCancel={() => setEditingChecklist(false)}
                onSave={(reviewChecklist) =>
                  patch.mutate({ reviewChecklist }, { onSuccess: () => setEditingChecklist(false) })
                }
              />
            ) : (
              <ul className="px-5 py-3 text-[13px]">
                {d.reviewChecklist.map((c, i) => (
                  <li key={i} className="border-b border-border py-1.5 last:border-0">
                    <span className="font-medium">{c.area}:</span> <span className="text-muted">{c.item}</span>
                  </li>
                ))}
                {!d.reviewChecklist.length && <li className="py-1.5 text-muted">No checklist items.</li>}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <Dialog
        open={Boolean(pending)}
        onClose={() => setPending(null)}
        title={pending === 'REVISIONS_REQUESTED' ? 'Ask for revisions' : 'Withdraw the submission'}
        description={
          pending === 'REVISIONS_REQUESTED'
            ? 'BlackWolf sees this note on the deliverable.'
            : `${client} sees this note in the submission log.`
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setPending(null)}>
              Cancel
            </Button>
            <Button
              loading={move.isPending}
              disabled={!note.trim()}
              onClick={() => pending && move.mutate({ to: pending, note: note.trim() })}
            >
              {pending === 'REVISIONS_REQUESTED' ? 'Send to BlackWolf' : 'Withdraw'}
            </Button>
          </>
        }
      >
        <Field label={pending === 'REVISIONS_REQUESTED' ? 'What needs to change' : 'Reason'} htmlFor="mv-note">
          <Textarea id="mv-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
        </Field>
      </Dialog>
    </div>
  );
}
